import { memo, useContext, useState } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';

import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { useFormContext } from "react-hook-form";
import SearchSelect from "@components/SearchSelect";
import { Checkbox, Input, Option, Select, Switch, Textarea, switchClasses } from "@mui/joy";
import { DeleteRowButton } from "./DeleteRowButton";
import { AddRowButton } from "./AddRowButton";
import { DatePicker } from "@components/forms/DatePicker";
import { dateFromTime, ensureDateObject, formatDateForDatabase } from "@utils/dates";
import { useAuth } from "@providers/AuthProvider";
import { TimePicker } from "@components/forms/TimePicker";
import { DynamicControl } from "@components/forms/DynamicControl";
import { additionalSx } from "@utils/additionalSx";
import { sortByDate } from "@utils/sorters";
import { FauxTable, FauxTableRow } from "@components/FauxTable";

interface BaseColumn {
  label: string;
  class?: string;


  type?: string;
  endpoint?: string;
  editable?: boolean;
  name?: string;
  options?: object[];
}

interface ColumnWithValue extends BaseColumn {
  // label: string;
  value: string;//|number|null;
  // class?: string;
  transformer?: never;
} //& CommonColumn

interface ColumnWithTransformer extends BaseColumn {
  // label: string;
  value?: never;
  // class?: string;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const ObjectRelationshipTasksEdit = memo(function ObjectRelationshipTasksEdit({
  // data = null, // parent object
  // setRelationship,
  relationship = '',//[],
  title,
  tint = null,
  sort = null,
  // columns,
  collapsible = false,//true,
  newRowData = null,
  sx = null,
  ...restProps
}) {
  const { user } = useAuth();
  const { control, register, setValue, getValues, watch } = useFormContext();
  const data = watch(relationship) ?? [];
  const [isExpanded, setIsExpanded] = useState(false);

  const perPage = 5;

  const paginatedRelationship = data;//(isExpanded || !collapsible) ? sortedRelationship : sortedRelationship.slice(0, perPage);

  const canCollapse = collapsible && data.length > perPage;
  const isEmpty = paginatedRelationship.length === 0;

  let paginationTitle = null;

  let isTableEnabled = true;
  if(restProps.hasOwnProperty('enabled')) {
    isTableEnabled = typeof restProps.enabled === 'function' ? restProps.enabled(watch()) : restProps.enabled;
  }

  if(canCollapse/*collapsible && !isEmpty*/) {
    let titleText:any = <></>;//'All';
    if(data.length > perPage) {
      const ToggleIcon = isExpanded ? UnfoldMoreRoundedIcon : UnfoldLessRoundedIcon;
      titleText = <>
        {/* {`${isExpanded ? relationship.length : perPage} of ${relationship.length}`} */}
        <ToggleIcon sx={{width: '1rem', height: '1rem', mt: '-3px'}} />
      </>;
    }

    paginationTitle = (
      <Typography
        className="pagination"
        sx={theme => ({
          px: 1,
          py: 0.25,
          fontSize: 'sm',
          transition: 'var(--mm-default-transition)',
          backgroundColor: /*theme.palette.background.popup*/'#FFFFFF6B',// tint ? theme.palette[tint][200] : theme.palette.background.level2,
          borderRadius: 'sm',
        })}
      >
        {titleText}
      </Typography>
    );
  }

  const addRow = (initialData = {}) => {
    const {id, ...restData} = initialData;

    // remove pivot data from contacts
    let contacts = restData?.contacts?.map((contact) => {
      const {pivot, ...restContact} = contact;
      return restContact;
    }) ?? [];

    let newRow = {
      is_unsaved: true,
      budget_type: 0,
      due_date: "0", // this is a hack because setting it to empty doesn't actually clear the date picker
      ...restData,
      contacts: contacts,
    };

    if(newRowData !== null) {
      try {
        newRow = {
          ...newRow,
          ...newRowData({
              user,
              relationship,
              data,
          }),
        };
      } catch(e) {
        //
      }
    }

    setValue(relationship, [newRow, ...data]);

    /*
    if(!initialData.hasOwnProperty('due_date')) {
      // forcefully reset date
      // this is a hack because setting it to empty doesn't actually clear the date picker
      // and if we try to modify it and add a task later on, it resets the date to the original state
      setValue(relationship+'.0.due_date', '0');
    }
      */
  }

  const markRowForDeletion = (rowIndex, mark = true) => {
    // let _data = data;
    // console.log(data[rowIndex]);
    data[rowIndex].to_be_deleted = mark;

    // if it's empty, just delete it
    const { to_be_deleted, is_unsaved, ...otherProps } = data[rowIndex];
    // console.log(otherProps);
    if(data[rowIndex].is_unsaved && !Object.keys(otherProps).length) {
      data.splice(rowIndex, 1);
    }

    setValue(relationship, data);
  }

  // this is the primary value for a row w/ pivot
  const setRowValue = (rowIndex, key, value) => {
    /*
    // for clearing
    if(data[rowIndex] === null) {
      data[rowIndex] = {
        is_unsaved: true
      }
      setValue(relationship, data);
      return;
    }
    */

   // null key is for primary objects that may have pivot data
   if(key === null) {
      const { to_be_deleted, is_unsaved, pivot } = data[rowIndex];
      data[rowIndex] = value ?? {};
      data[rowIndex].to_be_deleted = to_be_deleted;
      data[rowIndex].is_unsaved = is_unsaved;
      if(pivot && Object.keys(pivot).length) { // only set this if there's a value to set it to
        data[rowIndex].pivot = pivot;
      }
    } else {
      // check pivot
      // console.log(typeof key);
      if(~key.indexOf('pivot.')) {
        const pivotName = key.split('pivot.')[1];
        if(!data[rowIndex].pivot) {
          data[rowIndex].pivot = {};
        }

        data[rowIndex].pivot[pivotName] = value;
      }
      // standard
      else
      {
        data[rowIndex][key] = value;
      }
    }

    setValue(relationship, data);
  };

  /*
  const setRowPivotValue = (rowIndex, pivotName, value) => {
    if(!data[rowIndex].pivot) {
      data[rowIndex].pivot = {};
    }
    data[rowIndex].pivot[pivotName] = value;
    // console.log(data[rowIndex].pivot);
    setValue(relationship, data);
  };
  */

  // columns = columns.filter(c => c.visibleOnEdit !== false);

  const AddAction = () => (restProps.hasOwnProperty('customAction')
    ? <>{restProps.customAction}</>
    : <AddRowButton disabled={!isTableEnabled} onClick={()=>addRow()}/>
  );

  return (
    <ObjectRelationshipCard
      unpadded
      // empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 0,//1.5,
              // pointerEvents: canCollapse ? 'all' : 'none',

              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={3}
              width={'100%'}
            >
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={0.5}
                width={'100%'}
                sx={{
                  pointerEvents: canCollapse ? 'all' : 'none',
                  py: 1.5,
                  cursor: 'pointer',//canCollapse ? 'pointer' : 'text',
                }}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Typography sx={theme => ({fontWeight: '600', color: 'white' })}>{title}</Typography>
                {paginationTitle}
              </Stack>

              {/* {...restProps.customAction && ...restProps.customAction()} */}
              {/* <AddRowButton disabled={!isTableEnabled} onClick={addRow}/> */}
              <AddAction/>
            </Stack>
          </CardActions>
        </CardOverflow>
      }
      sx={theme => ({
        '--mm-card-height': 'auto',// isExpanded ? 'auto' : '250px',
        // ...(isExpanded ? {'--mm-card-height': 'auto'} : {}),
        // ...sx
        ...additionalSx(theme, sx),
      })}
    >
      {/* <CardOverflow> */}
      <Table
        stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
          'thead th': {
            borderRadius: '0 !important',
          },
          'tbody td': {
            // borderWidth: '0 !important',
            verticalAlign: 'top',
          },
        }}
      >
        <thead>
          <tr>
            <th>Task</th>
            <th>Due</th>
            <th>Budget</th>
            <th>Notes</th>
            {/* {columns.map((header:IColumn) => <th key={header.label} style={{width: header?.width ?? `${100 / columns.length}%`}}>{header.label}</th>)} */}
            <th className="min"></th>
          </tr>
        </thead>
        <tbody>
          {!isTableEnabled && restProps?.disabledMessage && (
            <tr>
              <td colSpan={5}>
                {restProps.disabledMessage}
              </td>
            </tr>
          )}
          {paginatedRelationship.length === 0 && (
            <tr>
              <td colSpan={5}>
                <AddRowButton disabled={!isTableEnabled} onClick={()=>addRow()} includeText/>
              </td>
            </tr>
          )}
        {data.map((row, rowIndex:number) => {
          const isNew = !!row.is_unsaved;
          const formData = data[rowIndex] ?? null;
          const isDeleted = data[rowIndex]?.to_be_deleted ?? false;
          // console.log(formData)

          return (
            <tr key={rowIndex} className={`${isDeleted && 'to-delete'}`}>
              <td>
                <Input
                  key={`${rowIndex}-task`}
                  variant="outlined"
                  placeholder={'e.g. Secure snacks for event'}
                  disabled={isDeleted}
                  value={formData.task_name ?? ''}
                  onChange={(e) => setRowValue(rowIndex, 'task_name', e.target.value)}
                  // sx={{minWidth: '100px', width: '1px', ...(cell?.sx ?? {})}}
                  // type={cell?.inputType ?? 'text'}
                  // {...(cell?.inputType === 'number') ? {step: 'any'} : {}}
                  // startDecorator={cell?.startDecorator}
                  // endDecorator={cell?.endDecorator}
                />

                <SearchSelect
                  key={`${rowIndex}-asignees`}
                  placeholder={'Assign task...'}
                  // {...fieldProps}
                  endpoint={'contacts'}
                  // include={cell?.include}
                  disabled={isDeleted}
                  multiple
                  onChange={(val, objVal) => {
                    // we have to remove the pivot data or it might assign to the wrong task
                    let strippedObjVal = objVal.map(val => {
                      const {pivot, ...strippedVal} = val;
                      return strippedVal;
                    });
                    setRowValue(rowIndex, 'contacts', strippedObjVal);
                  }}
                  value={formData?.contacts ?? []}
                  // groupBy={cell?.groupBy}
                  sx={{/*width: 'auto',*/ mt: 0.5}}
                  // hydrateWith={hydrateWith}
                  hydrateWith={formData?.contacts ?? []}
                />
              </td>
              <td>
                <DatePicker
                  key={`${rowIndex}-due`}
                  formatDensity="spacious"
                  // defaultValue={new Date()}
                  disabled={isDeleted}
                  value={formData.due_date ? ensureDateObject(formData.due_date) : null}
                  onChange={(value) => {
                    setRowValue(rowIndex, 'due_date', value.target.value /*formatDateForDatabase(value)*/)
                  }}
                  // {...fieldProps}
                />
              </td>
              <td>
                <FauxTable sx={{rowGap: 0.5}}>
                  <FauxTableRow
                    // title={formData.budget_type === 1 ? 'Income' : 'Expense'}
                    // titleSx={{minWidth: '4rem'}} // keep the layout from shifting
                    title={'Type'}
                  >
                    <Typography sx={{userSelect: 'none',}} component="label" startDecorator={
                    <Switch
                      key={`${rowIndex}-budgettype`}
                      variant={!isDeleted ? 'solid' : 'outlined'}
                      disabled={isDeleted}
                      onChange={(e) => {
                        setRowValue(rowIndex, 'budget_type', !formData.budget_type ? 1 : 0)
                      }}
                      checked={formData.budget_type === 1}
                      // checked={restProps?.reverse ? !(!!value) : !!value}
                      sx={theme => ({
                        '--Switch-trackBackground': theme.palette.danger[500],// '#EE5E52',
                        '&:hover': {
                          '--Switch-trackBackground': theme.palette.danger[600],
                        },
                        [`&.${switchClasses.checked}`]: {
                          '--Switch-trackBackground': theme.palette.success[500],
                          '&:hover': {
                            '--Switch-trackBackground': theme.palette.success[600],
                          },
                        },
                      })}
                    />
                    }>{formData.budget_type === 1 ? 'Income' : 'Expense'}</Typography>
                  </FauxTableRow>
                  <FauxTableRow title="Est.">
                    <Input
                      key={`${rowIndex}-est`}
                      variant="outlined"
                      // placeholder={cell?.placeholder ?? `${cell.label}...`}
                      disabled={isDeleted}
                      value={formData.estimated_budget ?? ''}
                      onChange={(e) => setRowValue(rowIndex, 'estimated_budget', e.target.value)}
                      // sx={{minWidth: '100px', width: '1px'}}
                      type={'number'}
                      step={'any'}
                      startDecorator={'$'}
                    />
                  </FauxTableRow>
                  <FauxTableRow title="Actual">
                    <Input
                      key={`${rowIndex}-actual`}
                      variant="outlined"
                      // placeholder={cell?.placeholder ?? `${cell.label}...`}
                      disabled={isDeleted}
                      value={formData.actual_budget ?? ''}
                      onChange={(e) => setRowValue(rowIndex, 'actual_budget', e.target.value)}
                      // sx={{minWidth: '100px', width: '1px'}}
                      type={'number'}
                      step={'any'}
                      startDecorator={'$'}
                      // size={'sm'}
                    />
                  </FauxTableRow>
                </FauxTable>
              </td>
              <td>
                <Textarea
                  key={`${rowIndex}-notes`}
                  variant="outlined"
                  minRows={1}
                  maxRows={3}
                  // key={`${rowIndex}-note`}
                  placeholder={'e.g. Reach out to Marsha about providing truffles'}
                  disabled={isDeleted}
                  value={formData.notes ?? ''}
                  onChange={(e) => setRowValue(rowIndex, 'notes', e.target.value)}
                />
              </td>
              {/*

              {columns.map((cell:IColumn, cellIndex:number) => {
                // console.log(cell);
                const isEditable = isNew || cell.editableAfterSave !== false;

                let cellType = cell?.type ?? 'text';
                let cellSelectOptions = [];
                if(cell.hasOwnProperty('options')) {
                  cellType = 'select';
                  cellSelectOptions= typeof cell.options === 'function' ? cell.options(formData, watch()) : cell.options;
                }
                let cellEnabled = !isDeleted;
                if(!isDeleted && cell.hasOwnProperty('enabled')) {
                  cellEnabled = typeof cell.enabled === 'function' ? cell.enabled(formData, watch()) : cell.enabled;
                }

                // console.log(cellType);

                // type: 'relationship',
                // endpoint: 'properties',

                let pivotValue = null;
                let pivotName = null;
                // console.log(cell);
                let cellName = cell?.column ?? cell?.name ?? '';
                if(cellName && cellName.indexOf('pivot.') !== -1) {
                  pivotName = cellName.split('pivot.')[1];
                  pivotValue = formData?.pivot?.[pivotName];// ?? null;
                }
                // console.log(pivotName);
                // const pivotValue = cell?.name?.indexOf('pivot.') !
                // const cellValue;
                //pivot.value
                if(!cellName && cell.type !== 'custom') {
                  console.log(cell);

                  console.warn('Empty cellName typically indicates column prop isn\'t beig passed. Check columnHelpers');
                }

                let hydrationData = [];
                const { to_be_deleted, is_unsaved, ...otherProps } = data[rowIndex];
                if(Object.keys(otherProps).length) {
                  hydrationData = [data[rowIndex]];
                }

                let nameForCell = null;
                let valueForCell = null;
                if(cellName && cellName.includes('pivot.')) {
                  nameForCell = cellName.split('pivot.')[1];
                  valueForCell = formData?.pivot?.[pivotName];// ?? null;
                } else {
                  nameForCell = cellName;
                  valueForCell = formData?.[nameForCell];
                }
                // console.log(formData);
                // console.log(cellName, valueForCell);

                // todo: modifiers for cell values

                return (
                  <td key={`${rowIndex}-${cellIndex}`} className={cell?.class}>
                    {isEditable === true && (<>
                      {/*
                      <Controller
                        name={name}
                        control={control}
                        // rules={{ required: true }}
                        render={({ field: {ref, ...fieldProps} }) => (
                          <SearchSelect
                            {...fieldProps}
                            endpoint={restProps.endpoint}
                            hydrateWith={hydrateWith}
                            // hydrateWith={getValues().spouse ? [getValues().spouse] : []}
                          />
                        )}
                      />
                      *\/}
                      {cellType === 'relationship' && (
                          <SearchSelect
                            key={cellIndex}
                            // {...fieldProps}
                            endpoint={cell.endpoint}
                            include={cell?.include}
                            disabled={!cellEnabled}
                            onChange={(val, objVal) => setRowValue(rowIndex, null, objVal)}
                            value={formData?.id ?? ''}
                            groupBy={cell?.groupBy}
                            sx={{width: 'auto'}}
                            // hydrateWith={hydrateWith}
                            hydrateWith={hydrationData}
                          />
                      )}
                      {cellType === 'select' && (
                        <Select
                          key={cellIndex}
                          variant="outlined"
                          placeholder={cell?.placeholder ?? "Choose..."}
                          disabled={!cellEnabled}
                          multiple={cell?.multiple}
                          renderValue={cell?.renderValue}
                          value={valueForCell ?? ''}
                          onChange={(e, v) => setRowValue(rowIndex, cellName, v)}
                          sx={{width: 'auto'}}
                          // value={}
                          // id={name}
                          // {...register(name, config)}
                          // defaultValue={defaultValue}
                          // {...restProps}
                          // name={name}
                        >
                          {cellSelectOptions.map((o, index) => (
                            <Option key={index} value={cell?.objectAsValue ? o : (o.hasOwnProperty('id') ? o.id : o.value)}>
                              {cell?.multiple && (
                                <Checkbox checked={Array.isArray(valueForCell) && valueForCell.indexOf(o.id) !== -1}/>
                              )}
                              {o?.name ?? o?.address ?? o?.unit ?? o.label}
                            </Option>
                          ))}
                        </Select>
                      )}
                      {cellType === 'boolean' && (
                        <Typography sx={{userSelect: 'none',}} component="label" startDecorator={
                          <Switch
                            key={cellIndex}
                            variant={cellEnabled ? 'solid' : 'outlined'}
                            disabled={!cellEnabled}
                            // id={name}
                            // type={type}
                            // {...register(name, config)}

                            // defaultValue={defaultValue}
                            // {...onChange}
                            onChange={(e) => {
                              // setValue(name, !(!!value))
                              console.log(e.target.value)
                              setRowValue(rowIndex, cellName, !valueForCell ? 1 : 0)

                              if(typeof cell.onChange === 'function') {
                                cell.onChange(formData, data, (_cell, val) => setRowValue(rowIndex, _cell, val));
                              }
                            }}
                            checked={!!valueForCell}
                            // checked={restProps?.reverse ? !(!!value) : !!value}
                          />
                        }>
                          {cell.label}
                        </Typography>
                      )}
                      {cellType === 'date' && (
                        <DatePicker
                          key={cellIndex}
                          formatDensity="spacious"
                          // defaultValue={new Date()}
                          value={valueForCell ? ensureDateObject(valueForCell) : ''}
                          // onChange={(e) => setRowValue(rowIndex, pivotName, e.target.value)}
                          onChange={(value) => {
                            setRowValue(rowIndex, cellName, formatDateForDatabase(value))
                          }}
                          // {...fieldProps}
                        />
                      )}
                      {cellType === 'time' && (
                        <TimePicker
                          key={cellIndex}
                          formatDensity="spacious"
                          // defaultValue={new Date()}
                          value={valueForCell ? dateFromTime(valueForCell) : null}
                          // onChange={(e) => setRowValue(rowIndex, pivotName, e.target.value)}
                          onChange={(value) => {
                            setRowValue(rowIndex, cellName, value)
                          }}
                          // {...fieldProps}
                        />
                      )}
                      {cellType === 'text' && (
                        <Input
                          key={cellIndex}
                          variant="outlined"
                          placeholder={cell?.placeholder ?? `${cell.label}...`}
                          disabled={!cellEnabled}
                          value={valueForCell ?? ''}
                          onChange={(e) => setRowValue(rowIndex, cellName, e.target.value)}
                          sx={{minWidth: '100px', width: '1px', ...(cell?.sx ?? {})}}
                          type={cell?.inputType ?? 'text'}
                          {...(cell?.inputType === 'number') ? {step: 'any'} : {}}
                          startDecorator={cell?.startDecorator}
                          endDecorator={cell?.endDecorator}
                        />
                      )}
                      {cellType === 'textarea' && (
                        <Textarea
                          key={cellIndex}
                          variant="outlined"
                          minRows={1}
                          maxRows={3}
                          // key={`${rowIndex}-note`}
                          placeholder={cell?.placeholder ?? `${cell.label}...`}
                          disabled={!cellEnabled}
                          value={valueForCell ?? ''}
                          onChange={(e) => setRowValue(rowIndex, cellName, e.target.value)}
                        />
                      )}
                      {cellType === 'custom' && (<>
                        {cell?.render?.edit({rowIndex, row, data, setRowValue})}
                      </>)}
                    </>)}
                    {isEditable !== true && (<>
                      {cell.value && row[cell.value]}
                      {cell?.render?.editDisabled && cell.render.editDisabled(row, data)}
                    </>)}
                  </td>
                )
              })}
              */}
              <td>
                <DeleteRowButton data={data} index={rowIndex} toggle={markRowForDeletion}/>
                {/* <DuplicateRowButton data={data} index={rowIndex}/> */}

                <Tooltip title={'Duplicate this task'}>
                  <IconButton
                    size="sm"
                    color="neutral"
                    onClick={() => {
                      addRow(formData)
                    }}
                    disabled={isDeleted}
                    sx={{
                      bgcolor: 'background.level1',
                      // '--variant-outlinedHoverBg': '#FFFFFF6B',
                      // '--variant-outlinedActiveBg': '#FFFFFFB8',// theme.palette.background.level1
                      '--IconButton-size': '1.65rem',
                      'svg': {
                        width: '1.05rem',
                        height: '1.05rem',
                      }
                    }}
                  >
                    <ContentCopyRoundedIcon/>
                  </IconButton>
                </Tooltip>
              </td>
            </tr>
          )
        })}
        </tbody>
      </Table>
      {/* </CardOverflow> */}

    </ObjectRelationshipCard>
  );
});

export default ObjectRelationshipTasksEdit;
